import React, { Component } from 'react';
import { Button, Form, Input, Modal, Table } from 'antd';
import { Pencil, Plus, Power, Search, Shapes } from 'lucide-react';
import Inherit from '../../../../core/inherit';
import withNavigate from '../../../../core/withNavigate';
import { EmptyState, FilterBar, FilterField, IconAction, IconActions, Layout, PageHeader, SectionCard, StatusTag, TableSkeleton } from '../../../../components/common';
import CategoriesController from './categories.controller';

function CategoryModal({ open, editing, saving, onCancel, onSubmit }) {
    const [form] = Form.useForm();
    React.useEffect(() => {
        if (open) form.setFieldsValue({ label: editing?.value || '' });
    }, [open, editing, form]);

    return (
        <Modal
            open={open}
            title={editing ? 'Editar categoría' : 'Nueva categoría'}
            okText={editing ? 'Guardar' : 'Crear'}
            cancelText="Cancelar"
            confirmLoading={saving}
            onCancel={onCancel}
            onOk={() => form.submit()}
            destroyOnClose>
            <Form form={form} layout="vertical" onFinish={onSubmit} requiredMark={false} className="!mt-4">
                <Form.Item
                    label="Nombre"
                    name="label"
                    rules={[
                        { required: true, whitespace: true, message: 'El nombre es requerido' },
                        { max: 80, message: 'Máximo 80 caracteres' }
                    ]}
                    extra={editing ? `Clave: ${editing.name} (no cambia, la usan los productos)` : 'Por ejemplo: Harina de Maíz'}>
                    <Input autoFocus placeholder="Nombre de la categoría" />
                </Form.Item>
            </Form>
        </Modal>
    );
}

/**
 * Mimesa's "Categorías": the product categories the catalog, the product form and the filters
 * offer. Categories are deactivated, never deleted (products keep their category's key).
 */
export class MimesaCategoriesPage extends Inherit(CategoriesController, Component) {
    state = { loading: true, categories: [], search: '', modalOpen: false, editing: null, saving: false };

    columns() {
        return [
            {
                title: 'Categoría',
                dataIndex: 'value',
                key: 'value',
                sorter: (a, b) => a.value.localeCompare(b.value, 'es'),
                render: (value) => <span className="font-medium text-surface-900">{value}</span>
            },
            {
                title: 'Clave',
                dataIndex: 'name',
                key: 'name',
                render: (name) => <code className="text-xs text-surface-500">{name}</code>
            },
            {
                title: 'Estado',
                dataIndex: 'active',
                key: 'active',
                filters: [
                    { text: 'Activa', value: true },
                    { text: 'Inactiva', value: false }
                ],
                onFilter: (value, record) => record.active === value,
                render: (active) => (
                    <StatusTag tone={active ? 'success' : 'neutral'} dot>
                        {active ? 'Activa' : 'Inactiva'}
                    </StatusTag>
                )
            },
            {
                title: 'Acciones',
                key: 'actions',
                align: 'right',
                width: 120,
                render: (_, record) => (
                    <IconActions>
                        <IconAction icon={<Pencil />} label="Editar" onClick={() => this.openEdit(record)} />
                        <IconAction
                            icon={<Power />}
                            label={record.active ? 'Desactivar' : 'Activar'}
                            danger={record.active}
                            onClick={() => this.toggleActive(record)}
                            confirm={
                                record.active
                                    ? {
                                          title: 'Desactivar categoría',
                                          description: 'Dejará de ofrecerse al crear productos y en los filtros. Los productos que la usan no cambian.',
                                          okText: 'Desactivar'
                                      }
                                    : undefined
                            }
                        />
                    </IconActions>
                )
            }
        ];
    }

    render() {
        const { contextHolder } = this.props;
        const { loading, categories, search, modalOpen, editing, saving } = this.state;

        return (
            <Layout current="categories">
                {contextHolder}
                <PageHeader
                    icon={<Shapes />}
                    title="Categorías"
                    description="Las categorías del catálogo: se usan al crear productos y en los filtros"
                    actions={
                        <Button type="primary" icon={<Plus />} onClick={() => this.openCreate()}>
                            Nueva categoría
                        </Button>
                    }
                />

                {loading && categories.length === 0 ? (
                    <TableSkeleton rows={8} columns={4} />
                ) : (
                    <div className="space-y-4">
                        <FilterBar className="!mb-0">
                            <FilterField label="Buscar" className="w-full sm:w-72">
                                <Input
                                    value={search}
                                    onChange={(e) => this.setSearch(e.target.value)}
                                    placeholder="Buscar categoría..."
                                    prefix={<Search className="text-surface-400" />}
                                    allowClear
                                />
                            </FilterField>
                        </FilterBar>

                        <SectionCard flush title="Categorías" description="Activas e inactivas">
                            <div className="overflow-x-auto">
                                <Table
                                    columns={this.columns()}
                                    dataSource={this.visibleCategories}
                                    loading={loading}
                                    rowKey="name"
                                    size="middle"
                                    pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `Total ${total} categorías` }}
                                    locale={{
                                        emptyText: (
                                            <EmptyState
                                                icon={<Shapes />}
                                                title="Sin categorías"
                                                description="Crea la primera categoría para poder registrar productos"
                                                action={
                                                    <Button type="primary" icon={<Plus />} onClick={() => this.openCreate()}>
                                                        Nueva categoría
                                                    </Button>
                                                }
                                            />
                                        )
                                    }}
                                />
                            </div>
                        </SectionCard>
                    </div>
                )}

                <CategoryModal
                    open={modalOpen}
                    editing={editing}
                    saving={saving}
                    onCancel={() => this.closeModal()}
                    onSubmit={(values) => this.saveCategory({ label: values.label.trim() })}
                />
            </Layout>
        );
    }
}

export default withNavigate(MimesaCategoriesPage);
